import React from 'react';
import { cn } from '~/lib/utils';
import themes from './HeaderTheme.module.css';
import styles from './MenuSurface.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface MenuSurfaceProps {
  /** Fixed per menu: deck 248, add 332, share 340, account 276. */
  width: number;
  theme?: HeaderTheme;
  children: React.ReactNode;
  className?: string;
  /** Labels the popup for assistive tech, e.g. "Deck". */
  label?: string;
  /**
   * What this surface IS to assistive tech. `menu` by default, which is what four of the five
   * consumers are.
   *
   * The fifth is the preview's text-colour panel, and it is a `group`: a saturation square, a hue
   * strip and a hex field. Nothing in it is a `menuitem`, and a `menu` that contains none promises
   * arrow-key navigation between options that do not exist — which is worse than the shell being
   * reused for something that is not a list. The FRAME is what is being reused here, and the frame is
   * all this component was ever about.
   */
  role?: 'menu' | 'group';
  style?: React.CSSProperties;
}

/**
 * The shell every header menu is built in. Widths are fixed per menu rather than content-driven,
 * because the four menus are read as one family and a menu that resizes to its longest row breaks
 * that family up.
 *
 * The shell owns the frame and nothing else. It used to own hover too — one shape that travelled
 * between rows, driven from here by pointer delegation so all four menus got it without knowing
 * about it. That is gone: hover is the row's own `:hover` background again, painted where the
 * pointer is and nowhere else. A shape that slides between rows animates the one thing a menu
 * should never make you wait for, which is the answer to "what am I about to press" — and it has to
 * be chased with delegation, z-index and travel memory to keep it honest. The row already knew.
 */
export const MenuSurface = React.forwardRef<HTMLDivElement, MenuSurfaceProps>(function MenuSurface(
  { width, theme: themeProp, children, className, label, role = 'menu', style },
  ref,
) {
  const theme = useV15Theme(themeProp);
  return (
    <div
      ref={ref}
      role={role}
      aria-label={label}
      className={cn(themes[theme], styles.surface, className)}
      style={{ width, ...style }}
    >
      {children}
    </div>
  );
});

/**
 * The only separator in the system: 4px of air. There are no dividers anywhere in v1.5 — a rule
 * across a menu adds a second reading order on top of the one the spacing already gives.
 */
export function MenuSpacer() {
  return <div className={styles.spacer} aria-hidden />;
}
